/**
 * The API client.
 *
 * Same-origin only, and deliberately small. Two things here are not style choices:
 *
 * `credentials: 'same-origin'` — the session lives in an HttpOnly cookie, so nothing reads or
 * writes a token from JavaScript. There is no token in localStorage to steal.
 *
 * No error body is shown to the user verbatim. The API is careful about what it reveals — one
 * answer for unknown, expired and already-spent links, and no detail on why an Entra callback
 * failed — and a client that rendered `detail` straight onto the page would undo that.
 */

export type ApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false
      status: number
      /**
       * The API's own machine code for a refusal (`{"error":"challenge_failed"}`), when it sent one and
       * it is a plain code. Never shown to the user: callers branch on it, so a 503 from the API saying
       * "the bot check is unavailable" can be told apart from a 503 because the whole API is down.
       */
      code?: string
    }

/**
 * The `error` code in a refusal's JSON body, if it is a short snake_case word. Anything else (HTML from a
 * proxy, a long message, a body that is not JSON) is ignored: only a fixed vocabulary is read, so the
 * rule above, that no error body is rendered, still holds.
 */
async function refusalCode(response: Response): Promise<string | undefined> {
  try {
    const parsed: unknown = JSON.parse(await response.text())
    const code = (parsed as { error?: unknown } | null)?.error
    return typeof code === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(code) ? code : undefined
  } catch {
    return undefined
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })

    if (!response.ok) {
      const code = await refusalCode(response)
      return code ? { ok: false, status: response.status, code } : { ok: false, status: response.status }
    }

    // 202 and 204 carry no body. Parsing them would throw on the happy path.
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return { ok: true, data: undefined as T }
    }

    const text = await response.text()
    return { ok: true, data: (text ? JSON.parse(text) : undefined) as T }
  } catch {
    // Network failure. Status 0 rather than a thrown exception, so every caller handles the
    // offline case on the same branch as a refusal instead of forgetting it.
    return { ok: false, status: 0 }
  }
}

/** The plan a workspace is on. 'monitor' is the paid, continuous-monitoring tier (migration 0136). */
export type Plan = 'free' | 'monitor'

/** A subscription's lifecycle state (migration 0002). */
export type PlanStatus = 'none' | 'active' | 'past_due' | 'canceled'

export interface Me {
  email: string
  name: string | null
  workspaceId: string | null

  /**
   * The workspace's name, from the session.
   *
   * Null while a session has no workspace, which is normal during onboarding. The shell renders
   * this above a page of that tenant's findings, so it must come from the server: a name the
   * browser chose would be a label the browser put on somebody else's numbers.
   */
  workspaceName: string | null
  setupState: 'needs_workspace' | 'has_workspace'

  /**
   * The session-selected workspace's plan context, so the shell shows the REAL plan rather than a
   * hardcoded "free". All three are null for a session with no workspace (onboarding) — "no
   * workspace" and "workspace not paying" are different answers and must stay distinguishable.
   *
   * `monitoringActive` is the server's CANONICAL entitlement (`auth.is_on_monitor`), not re-derived
   * from `plan` on the client — the shell trusts this boolean for whether monitoring is on.
   */
  plan: Plan | null
  planStatus: PlanStatus | null
  monitoringActive: boolean | null

  /**
   * Whether this build assesses Azure (Azure CSPM switched on). The Cloud page and its nav entry
   * exist only then. Absent from an older server: no Cloud page.
   */
  assessesAzure?: boolean

  /** Whether this build has the AI area (AiPosture:Enabled). The AI page and its nav entry exist only then. */
  assessesAiPosture?: boolean

  /** Whether this workspace previews the redesigned dashboard (Dashboard:PreviewWorkspaces). */
  dashboardPreview?: boolean
}

/**
 * The signed-in caller, with 401 distinguished from a failure to ask.
 *
 * `getMe` collapses both into null, which is right for "should I show the sign-in screen" and
 * wrong for anything that uses the answer as an identity. A transient 500 looked exactly like a
 * signed-out visitor, so a check that two responses described the same workspace passed by
 * default at the moment it was most needed.
 */
export async function fetchMe(): Promise<ApiResult<Me | null>> {
  const result = await request<Me>('/api/auth/me')

  // A visitor. An answer, not a failure.
  if (!result.ok && result.status === 401) return { ok: true, data: null }

  return result
}

/** The signed-in caller, or null. 401 is the normal answer for a visitor, not an error. */
export async function getMe(): Promise<Me | null> {
  const result = await fetchMe()
  return result.ok ? result.data : null
}

/**
 * Asks for a sign-in link.
 *
 * Returns only whether the request REACHED the server, and nothing about the address.
 *
 * The endpoint answers 202 with the same body whether the address is known, suppressed or rate
 * limited, because a response that differed would turn an unauthenticated endpoint into an
 * account-enumeration oracle — and the customer list of a security product is itself sensitive.
 * So the UI says "check your inbox" for every one of those.
 *
 * A transport failure or a 5xx is a different thing and is worth telling the truth about. It
 * says nothing about the address — it happens identically for one that does not exist — so
 * surfacing it leaks nothing, while swallowing it would tell someone to wait for mail that was
 * never asked for.
 */
export async function requestMagicLink(
  email: string,
  company: string,
  turnstileToken?: string | null,
): Promise<{
  reachedServer: boolean
  confirmationCode: string | null
  /**
   * What the bot check said, when the check is on.
   *
   * 'failed' (400 `challenge_failed`) is the person's to retry: the token was missing, expired or
   * already used. It is the ONE answer other than 202 this endpoint gives for a request that reached it,
   * and it depends on the token alone, never on the address, so telling the person leaks nothing about
   * who has an account. 'unavailable' (503 `challenge_unavailable`) is Cloudflare, or our own setting
   * for it: not the person's doing and not a pass. Without this, a 400 would fall into "reached the
   * server" below and the page would announce "check your inbox" for mail that was never requested.
   */
  challenge: 'ok' | 'failed' | 'unavailable'
}> {
  const result = await request<{ confirmationCode?: string }>(
    '/api/auth/magic-link/request',
    {
      method: 'POST',
      // The token is left out when there is none, so the body is exactly what it was before the check
      // existed whenever the check is off.
      body: JSON.stringify(
        turnstileToken ? { email, company, turnstileToken } : { email, company },
      ),
    },
  )

  // By the API's own code, not by status alone: a 503 can just as well mean the whole API is down (a
  // proxy answering for it), and blaming the security check for that would send the person to retry
  // the wrong thing. Only a refusal that SAYS it is the check is read as the check.
  const challenge: 'ok' | 'failed' | 'unavailable' =
    !result.ok && result.status === 400 && result.code === 'challenge_failed'
      ? 'failed'
      : !result.ok && result.status === 503 && result.code === 'challenge_unavailable'
        ? 'unavailable'
        : 'ok'

  // status 0 is a network failure; 5xx is ours. Every other status — including the 202 the
  // endpoint always gives — means the request was received and the answer is deliberately
  // uninformative. A refused check (400) was received but is NOT a sent link: callers look at
  // `challenge` first.
  const reachedServer = result.ok || (result.status !== 0 && result.status < 500)

  // Shown on this screen and nowhere else. It is the half of the credential that does not
  // travel through the customer's mail pipeline, so it must never be put anywhere that
  // pipeline — or a log, or a URL — can reach.
  const confirmationCode = result.ok ? result.data?.confirmationCode ?? null : null

  return { reachedServer, confirmationCode, challenge }
}

/**
 * What the sign-in page needs to know before anyone has signed in: the public Turnstile site key, or
 * null when the bot check is off.
 *
 * `reached` says whether the answer was had at all. A failure is NOT "the check is off": the page must
 * not draw an empty form and let a request go out that the server will refuse for want of a token, with
 * no widget to pass.
 */
export async function getAuthConfig(): Promise<{ turnstileSiteKey: string | null; reached: boolean }> {
  const result = await request<{ turnstileSiteKey?: string | null }>('/api/auth/config')

  return result.ok
    ? { turnstileSiteKey: result.data?.turnstileSiteKey ?? null, reached: true }
    : { turnstileSiteKey: null, reached: false }
}

/**
 * Spends a sign-in link.
 *
 * The browser that requested the link holds an HttpOnly binding cookie and is admitted without
 * a code. Any other device has to supply the code shown on the requesting screen, because the
 * link by itself is disclosed to Exchange Online, Safe Links and URL-click telemetry.
 */
export async function consumeMagicLink(
  token: string,
  code?: string,
): Promise<{ ok: boolean; email: string | null }> {
  const result = await request<{ email?: string }>('/api/auth/magic-link/consume', {
    method: 'POST',
    body: JSON.stringify({ token, code: code ?? null }),
  })

  return result.ok
    ? { ok: true, email: result.data?.email ?? null }
    : { ok: false, email: null }
}

/** Starts Entra sign-in. Returns the URL to send the browser to, or null if it could not start. */
/**
 * Begins Microsoft sign-in.
 *
 * @param afterSignOut
 * True when the person arrived here by signing out, which makes the server ask Microsoft for
 * `prompt=login` rather than `prompt=select_account` -- so somebody who signed out on a shared
 * machine has to present credentials again rather than being waved back in by a cookie the
 * identity provider still holds.
 */
export async function startEntraSignIn(afterSignOut = false): Promise<string | null> {
  const result = await request<{ authorizeUrl: string }>(
    `/api/auth/entra/start${afterSignOut ? '?signed_out=1' : ''}`, {
    method: 'POST',
  })

  return result.ok ? result.data.authorizeUrl : null
}

/**
 * Starts attaching a Microsoft identity to the account you are already signed in to.
 *
 * NOT a sign-in. The two look identical from the browser -- both end at Microsoft and come back --
 * and they are opposites: sign-in asks Microsoft who you are, this tells Microsoft's answer where
 * to attach. That is why it is a separate endpoint with its own binding cookie rather than a flag
 * on sign-in.
 *
 * It exists because migration 0102 stopped an assertion joining an existing account. An address
 * inside a token describes an account; anyone who can create a directory can assert one. A live
 * session is proof of holding the account, so linking happens from inside one.
 */
export async function startMicrosoftLink(
  returnTo?: 'connect',
): Promise<ApiResult<{ authorizeUrl: string }>> {
  // `return` picks where the callback sends the browser afterwards: Settings (the default) or the Connect
  // screen. The server honours only the exact word "connect"; it is a destination, never a decision.
  const query = returnTo === 'connect' ? '?return=connect' : ''

  return request<{ authorizeUrl: string }>(`/api/auth/entra/link/start${query}`, { method: 'POST' })
}

export interface PendingInvitation {
  workspaceId: string
  workspaceName: string
  role: string
  invitedAt: string
}

/**
 * The invitations you have been sent and not acted on.
 *
 * Nothing in this product could show these until migration 0102, which is exactly why every
 * sign-in used to accept all of them silently -- with no way to display an invitation, signing in
 * was made to stand in for agreeing to one.
 */
/**
 * Invitations waiting for the signed-in person.
 *
 * Returns the RESULT, not a list. This used to collapse every failure into `[]`, so a network
 * blip or an expired session rendered exactly like "nobody has invited you" -- and the Settings
 * card, seeing an empty list, vanished without a word. A caller has to be able to tell "none" from
 * "could not ask". Found by the independent review.
 */
export interface WorkspaceChoice {
  workspaceId: string
  workspaceName: string
  role: string
}

/**
 * The workspaces the signed-in person may enter.
 *
 * Shown as a chooser when sign-in did not auto-place them -- they belong to several, or have a
 * pending invitation. `current` is the workspace the session already names, if any.
 */
export async function fetchWorkspaces(): Promise<
  ApiResult<{ current: string | null; workspaces: WorkspaceChoice[] }>
> {
  return request<{ current: string | null; workspaces: WorkspaceChoice[] }>('/api/auth/workspace/list')
}

export async function fetchInvitations(): Promise<ApiResult<{ invitations: PendingInvitation[] }>> {
  return request<{ invitations: PendingInvitation[] }>('/api/auth/invitations')
}

/**
 * Points a session that names NO workspace at one you belong to.
 *
 * Narrower than it sounds, and deliberately. auth.attach_session_to_workspace moves a session
 * forwards and only from nothing: re-pointing a live session between tenants would change the
 * meaning of every capability already minted from it, so migration 0064 refuses it outright --
 * two workspaces means two sign-ins.
 *
 * What this recovers is the gap between accepting an invitation and being placed in it, which are
 * separate writes. Answers `attached: false` with a reason when the session is already placed,
 * rather than pretending it moved.
 */
export async function selectWorkspace(
  workspaceId: string,
): Promise<ApiResult<{ attached: boolean; reason: string | null }>> {
  return request<{ attached: boolean; reason: string | null }>('/api/auth/workspace/select', {
    method: 'POST',
    body: JSON.stringify({ workspaceId }),
  })
}

export async function acceptInvitation(
  workspaceId: string,
): Promise<ApiResult<{ accepted: boolean; attached: boolean }>> {
  return request<{ accepted: boolean; attached: boolean }>('/api/auth/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ workspaceId }),
  })
}

/** Removes a pending invitation without joining, so it stops blocking your own onboarding. */
export async function declineInvitation(
  workspaceId: string,
): Promise<ApiResult<{ declined: boolean }>> {
  return request<{ declined: boolean }>('/api/auth/invitations/decline', {
    method: 'POST',
    body: JSON.stringify({ workspaceId }),
  })
}

/**
 * Ends this session, and every session refreshed from it.
 *
 * The server revokes the whole token FAMILY, not one token: a stolen refresh token rotated moments
 * earlier would otherwise survive its victim signing out.
 *
 * Answers 204 whether or not there was a session, so there is nothing to branch on. This existed
 * and nothing called it -- the avatar in the app bar was a button with no handler, so there was no
 * way to sign out of this product at all.
 */
/**
 * True once the server has revoked the session and cleared its cookie (a 204). False on a network
 * failure or an error answer, when the HttpOnly cookie is still in the browser and the session may
 * still be live -- which the caller must say, never present as a completed sign-out.
 */
export async function signOut(): Promise<boolean> {
  const result = await request('/api/auth/sign-out', { method: 'POST' })
  return result.ok
}

// ── admin consent ──────────────────────────────────────────────────────────────────────────
//
// The shape here is not incidental. The callback from Microsoft cannot attach a tenant -- it only
// stages what it saw -- so the browser has to come back and ask what is waiting, show it, and
// confirm it with a POST. That POST is the security boundary: a navigation cannot produce one,
// which is exactly what a forged callback is.

export interface StagedConsent {
  /** The directory about to be attached. Shown to the person, not just posted back. */
  tenantId: string
  expiresAt: string
}

export type ConsentStatus =
  | 'verified'
  | 'pending_verification'
  | 'connected_elsewhere'
  | 'tenant_mismatch'
  | 'nothing_pending'
  | 'refused'

/**
 * Creates the workspace a session does not have yet.
 *
 * Called from the connect screen rather than offered as a step of its own: a workspace with no
 * tenant attached is not a thing a customer wants, so it is created on the way to consent rather
 * than presented as a decision. Safe to call twice -- the server answers created:false for a
 * session that already has one instead of making a second.
 */
export async function createWorkspace(name: string | null): Promise<ApiResult<{ created: boolean }>> {
  return request('/api/auth/workspace', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

/**
 * Asks for a new assessment.
 *
 * Sends nothing. The workspace comes from the session and the tenant from that workspace, so
 * there is no argument with which to ask for somebody else's scan.
 *
 * Safe to call twice. One pending-or-claimed request is allowed per connection, so a second press
 * queues nothing and still answers 202 -- 'queued' and 'already_queued' are the same news from
 * here, and telling them apart would only invite the UI to report the second as a failure.
 */
export async function requestScan(): Promise<ApiResult<{ status: string }>> {
  return request('/api/scans', { method: 'POST' })
}

export async function startConsent(): Promise<ApiResult<{ consentUrl: string }>> {
  return request('/api/auth/consent/start', { method: 'POST' })
}

export async function getStagedConsent(): Promise<ApiResult<StagedConsent>> {
  return request('/api/auth/consent/staged')
}

/**
 * Confirms the tenant that was DISPLAYED.
 *
 * The id is echoed back rather than left implicit, because staging deliberately does not consume
 * the request -- so a tenant could be staged underneath a page that is already open. Sending back
 * what was rendered means the server can refuse a confirmation for anything else, and the person
 * cannot read one directory and authorise another.
 */
export async function confirmConsent(
  tenantId: string,
): Promise<ApiResult<{ status: ConsentStatus; tenantId?: string }>> {
  return request('/api/auth/consent/confirm', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  })
}

export async function declineConsent(): Promise<ApiResult<{ status: string }>> {
  return request('/api/auth/consent/decline', { method: 'POST' })
}

/** Retries verification for a connection left pending. */
export async function verifyConsent(): Promise<ApiResult<{ status: ConsentStatus }>> {
  return request('/api/auth/consent/verify', { method: 'POST' })
}

// ── findings ───────────────────────────────────────────────────────────────────────────────
//
// Note what is NOT here: no workspace or tenant parameter on any of these. The server derives it
// from the session, so the client cannot ask for the wrong one -- and there is no id for a bug in
// this file to get wrong.

/**
 * How current an answer is.
 *
 * Travels with every read rather than being fetched separately, because a screen that forgets to
 * ask is a screen that shows a partial scan as a clean one. A tenant where half the checks could
 * not run has fewer findings than a healthy tenant, and that is the one direction this product
 * must never round in.
 */
export interface ScanFreshness {
  scanId: string | null
  status: 'running' | 'complete' | 'partial' | 'failed' | null
  asOf: string | null
  areasTotal: number
  areasCompleted: number
  failureReason: string | null

  /** Each area of this scan and where it has got to: pending, running, complete or failed. */
  areas: ScanAreaProgress[]

  /**
   * How many of `areasCompleted` are complete only because NOTHING in them is licensed (migration
   * 0158). Those areas finished and were not assessed. Absent from an older server: read as none.
   *
   * Kept apart from the counts rather than taken out of them, so `complete` keeps its one meaning
   * (nothing failed, every area finished) -- a tenant that never bought Exchange is not told its
   * assessment is partial. What this adds is the line between "complete because not licensed" and
   * "complete and assessed", which the counts alone cannot draw.
   */
  areasNotLicensed?: number

  /**
   * The controls this scan did not assess because the tenant has no licence for the Microsoft
   * service they read AND Microsoft refused the read (0158, owner 2026-09-26), by area and service.
   * Includes an area that assessed its other controls (Defender for Endpoint in Endpoint, beside
   * Intune). Empty on a tenant licensed for everything the scan reads; absent from an older server.
   */
  notLicensed?: NotLicensedControls[]

  /** A scan is queued and has not run. An empty dashboard here does NOT mean clean. */
  scanPending: boolean

  /**
   * Whether these numbers can be read as a whole answer.
   *
   * Computed by the server from the status AND the area counts together, so a scan claiming to be
   * complete over three of five areas is not complete here. Never re-derive this from `status` on
   * the client: that was the bug, and it resolved an inconsistent record in the reassuring
   * direction.
   */
  complete: boolean

  /** Status says complete, area counts disagree. A defect in the scan, surfaced rather than hidden. */
  inconsistent: boolean
}

/** One area of a scan, and where it has got to. */
export interface ScanAreaProgress {
  area: string
  status: string
  detail: string | null

  /**
   * The area is complete only because EVERY control in it was not assessed for want of a licence
   * (scan_area.not_licensed, 0158). Not assessed, so it must never be drawn as clean: "Email 0" and
   * "No open findings in Email" are exactly what a clean Email looks like. Absent from an older
   * server: read as false.
   */
  notLicensed?: boolean

  /**
   * The checks `detail` names as having seen only part of what they needed, by title. They ran and
   * their findings are reported, but each could not read, or could not judge, part of what it
   * needed, so they closed nothing they did not see. Newer scans may carry fixed diagnostic codes
   * and counts; old scans never acquire a reason inferred from today's check logic.
   *
   * Empty when the detail names none in the form the server recognises -- which, with a detail
   * present, is NOT "none were partial": the page then shows the detail itself. Absent from an
   * older server.
   */
  partlyRead?: PartlyReadCheck[]

  /**
   * The area's `detail` was recorded but could not be read back (a key gone, a row sealed under
   * an earlier binding, or an unsupported structured record), so `detail` is null and `partlyRead` empty WITHOUT meaning there was nothing
   * to say. A page must not call such an area read in full. Absent from an older server, which sent
   * null for both and could not tell them apart: read as false.
   */
  detailUnreadable?: boolean
}

/** A check that ran and saw only part of what it needed, named for a person. */
export interface PartlyReadCheck {
  key: string
  /** The catalogue title, or the key itself when there is none -- never blank. */
  title: string
  /** Fixed codes and counts recorded at scan time. Missing/empty means no per-check reason was recorded. */
  gaps?: { code: string; count: number }[]
}

/**
 * Controls in one area that a scan did not assess because the tenant is not licensed for the
 * Microsoft service they read, and Microsoft refused the read (0158, owner 2026-09-26).
 *
 * Never counted as passing, never closing a finding, and never in the "could not be read" card: a
 * known fact about the tenant's subscription, told to the customer as such.
 */
export interface NotLicensedControls {
  /** The area the controls belong to: Email, Data, Endpoint. */
  area: string

  /** The service's stable code, for branching on. `string` too: a service added later still renders. */
  service: 'exchange_online' | 'sharepoint' | 'defender_for_endpoint' | string

  /** The service as a customer names it, e.g. "Exchange Online". The words a page shows. */
  serviceName: string

  /** How many controls in that area were not assessed for this service. */
  controls: number

  /** Every control in the area was one of these: the area was not assessed at all. */
  wholeArea: boolean

  /**
   * The sentence the scan recorded, written for ONE control ("so this control was not
   * assessed"). Pages that speak about several controls at once use their own words.
   */
  reason: string
}

export interface FindingSummary {
  id: string
  catalogKey: string
  title: string
  area: string
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational'

  /** Whose judgement this is. "Microsoft says high" and "we say high" are different claims. */
  severitySource: string

  /**
   * 'withdrawn' (migration 0151): Defenssive stopped assessing this -- its check was retired -- so
   * it is no longer current. Not fixed, and never shown as fixed: no assessment saw it go away.
   */
  status: 'active' | 'resolved' | 'suppressed' | 'withdrawn'

  /** When a completed assessment stopped seeing it. Null while open; absent from an older server. */
  resolvedAt?: string | null

  /**
   * How a RESOLVED finding was closed (migration 0176): 'resolved' when an assessment looked and did
   * not see it (or its check answered, on the tenant's configuration, that the control does not
   * apply), 'not_applicable' when an assessment closed it as not applicable -- because the workspace's
   * organisation profile said its control does not apply, or because its check answered not
   * applicable on the tenant's licences -- and none since has reached a verdict on that control. Null
   * unless resolved; absent from an older server, which only ever called both "resolved". A
   * 'not_applicable' finding is never shown as fixed.
   */
  closedAs?: 'resolved' | 'not_applicable' | null

  /** When its check was retired and it stopped being assessed. Set only on a withdrawn finding. */
  withdrawnAt?: string | null

  /**
   * One plain line that tells this finding apart from others with the same title -- e.g. a
   * vulnerability band: "Critical · 12 vulnerabilities on 4 devices". Counts and labels only, never
   * a tenant identifier (it is shown in lists). Null for most findings; absent from an older server.
   */
  summary?: string | null

  /** How much work the fix is. Lets the list show an effort chip and sort by impact × effort. */
  effort: 'Low' | 'Medium' | 'High'

  /** The job this finding belongs to, and its one ordering caveat. Null when ungrouped. */
  workstream: string | null
  sequenceHint: string | null

  /**
   * The CISA SCuBA policy id this finding is raised against, e.g. MS.AAD.3.2v2. `null` is a real
   * answer -- one of Defenssive's own checks, outside CISA SCuBA. ABSENT (an older server) means not
   * recorded, and is neither: the list's Standard filter files such a row under no standard.
   */
  scubaReference?: string | null

  /**
   * The workspace member carrying it (their email), or `null` when nobody has taken it. ABSENT (an
   * older server) means not recorded: the list's Owner filter treats such a row as neither assigned
   * nor unassigned.
   */
  owner?: string | null

  firstSeenAt: string
  lastSeenAt: string
}

export interface Overview {
  /** Which workspace answered. Compared against the session, never sent by the client. */
  workspaceId: string
  freshness: ScanFreshness
  activeBySeverity: Record<string, number>
  activeByArea: Record<string, number>

  /** Findings set aside (suppressed / not-applicable / accepted). Zero active with this non-zero is
   *  NOT a clean tenant — the dashboard must not say "nothing needs fixing" over it. */
  suppressed: number

  /** Controls the most recent scan tried to read and could not (could_not_run) — a control silently
   *  going dark. Named, so the customer sees which one, not just that coverage slipped. Empty on a
   *  healthy scan. Does NOT include licence gaps (skipped), which are steady and shown elsewhere,
   *  nor controls not assessed because the tenant is not licensed for their service (notLicensed). */
  unreadControls: UnreadControl[]

  /**
   * The same list as `freshness.notLicensed` (0158), repeated by the server beside unreadControls.
   * The pages read the freshness copy, so the dashboard's rows, its card and the freshness line all
   * come from one list and cannot disagree. Absent from an older server.
   */
  notLicensed?: NotLicensedControls[]

  /**
   * Microsoft Secure Score as Microsoft reported it on the latest assessment that read it. Null when
   * no assessment has read it yet; absent from an older server. MICROSOFT'S number, not ours: the
   * product publishes no score of its own, and every surface showing this must say whose it is.
   */
  secureScore?: SecureScoreReading | null

  /**
   * The areas this build assesses (from the registered checks). The dashboard draws its area rows
   * from these, so an area that no longer exists -- Cloud, retired 2026-09-25 -- is not shown as
   * "assessed, 0" because the last scan before the change still had a row for it. Absent from an
   * older server: fall back to the scan's own areas.
   */
  assessedAreas?: string[]

  /**
   * What the latest assessment read from Microsoft Defender for the Endpoint page (0153): the
   * exposure score and the vulnerability counts. Null until one has tried; absent from an older
   * server. MICROSOFT'S numbers.
   */
  defender?: DefenderReading | null

  /** Active findings by area, then severity (the redesigned dashboard's bars). Absent from an older server. */
  activeByAreaAndSeverity?: Record<string, Record<string, number>>

  /** When the oldest active Critical or High finding was first seen, or null when none is open. */
  oldestCriticalHighFirstSeen?: string | null

  /** Active Critical or High findings with no owner. */
  unownedCriticalHigh?: number

  /**
   * How many days a finding of each severity may stay open (owner, 2026-10-11: Critical 14, High 30,
   * Medium 60, Low 90), as the server counted against them. Absent from an older server.
   */
  remediationTargetDays?: Record<string, number>

  /** Active findings open longer than their severity's target, by severity, from first seen. */
  overdueBySeverity?: Record<string, number>

  /** Set-aside findings whose review date falls within 30 days (or has passed). */
  setAsideReviewsDue?: number

  /** Each area's insights strip: past target, quick wins, no owner, oldest open. Absent from an older server. */
  areaInsights?: Record<string, { pastTarget: number; quickWins: number; unowned: number; oldestFirstSeen: string | null }>
}

/** Open findings at the last finished assessment of each of the past 30 days (dashboard slice 2). */
export interface OverviewTrend {
  workspaceId: string
  /** One per UTC day that had a finished assessment, oldest first. A day without one is absent. */
  points: { day: string; assessedAt: string; open: number; criticalHigh: number }[]
  /** Microsoft Secure Score's last measured reading of each day, oldest first. */
  secureScore: { day: string; currentScore: number; maxScore: number }[]
}

export async function getOverviewTrend(): Promise<ApiResult<OverviewTrend>> {
  return request<OverviewTrend>('/api/overview/trend')
}

/** One Defender reading, taken during an assessment (migration 0153). */
export interface DefenderReading {
  exposure: {
    /** measured, absent (Microsoft has none) or unavailable (not read). */
    status: 'measured' | 'absent' | 'unavailable' | string
    /** 0-100; LOWER is better. Microsoft's levels: 0-29 low, 30-69 medium, 70-100 high. */
    score: number | null
    measuredAt: string | null
    /** Why it was not read, as a short word (not_consented, forbidden, ...). */
    failure: string | null
  }
  vulnerabilities: {
    status: 'measured' | 'absent' | 'unavailable' | string
    /** Every open vulnerability Microsoft reports. */
    open: number | null
    /** Of those, the ones with a known exploit -- the only ones that are findings. */
    withKnownExploit: number | null
    /** Microsoft returned more than one assessment reads: the counts are a minimum. */
    truncated: boolean
    failure: string | null
  }
  readAt: string

  /**
   * What Microsoft Defender listed about the tenant's devices on the same read (0159). Null or absent
   * on a reading taken before 0159, which the page renders exactly as it did then.
   *
   * onboarded: devices Defender lists as onboarded -- discovered-only devices excluded. incomplete:
   * the list was cut short, so onboarded is a minimum, never an exact count (and never "none").
   *
   * failure (pre-0159 words or 0159 reason codes, as on exposure/vulnerabilities) says why the list
   * was not read when status is not 'measured'.
   */
  devices?: {
    status: 'measured' | 'unavailable' | string
    onboarded: number | null
    incomplete: boolean
    failure: string | null
  } | null
}

/** One reading of Microsoft Secure Score, taken during an assessment (migration 0151). */
export interface SecureScoreReading {
  /**
   * measured: read, numbers below. absent: the tenant has no Secure Score (e.g. not licensed) -- a
   * fact about the tenant. unavailable: we could not read it this time -- nothing is concluded.
   */
  status: 'measured' | 'absent' | 'unavailable'
  currentScore: number | null
  maxScore: number | null

  /** The date Microsoft computed the score (its createdDateTime), not when we read it. */
  measuredAt: string | null

  /** When Defenssive read it, i.e. the assessment it came from. */
  readAt: string

  /**
   * Microsoft's own category breakdown (Identity, Data, Device, Apps, …): points earned out of the
   * points available across that category's controls. Empty when not measured, or when Microsoft
   * did not return enough to compute it.
   */
  categories: SecureScoreCategory[]
}

export interface SecureScoreCategory {
  category: string
  score: number
  maxScore: number
}

/** One control a scan attempted and could not read, named for a person. */
export interface UnreadControl {
  key: string
  /** The human title, or the key itself when the control has no catalogue row — never blank. */
  title: string
  /** The area (Identity, Endpoint, …), or null when uncatalogued. */
  domain: string | null

  /**
   * could_not_run: attempted and got no usable answer. withheld (migration 0152): the check is
   * waiting on something in the tenant that is already raised as its own finding -- named in
   * waitsOn -- so the page says that instead of blaming permissions or Microsoft. Absent from an
   * older server: treat as could_not_run.
   */
  outcome?: 'could_not_run' | 'withheld'
  waitsOn?: { key: string; title: string | null } | null

  /**
   * Why this control produced no verdict, as one fixed word (migration 0159): not_connected,
   * access_not_issued, permission_not_granted, microsoft_refused, not_available_in_tenant, throttled,
   * microsoft_unavailable, dns_lookup_failed, no_data_from_microsoft, no_devices_onboarded,
   * incomplete_read, unexpected_response, beyond_check_scope, exempt_list_unreadable,
   * defenssive_configuration, check_failed, unclassified, awaiting_prerequisite. Null or absent:
   * recorded before 0159, and worded as it was then. A string, not a union, so a word a newer server
   * adds still type-checks -- unreadReasons.ts decides how an unknown one reads.
   */
  reason?: string | null

  /**
   * Only with permission_not_granted: the Microsoft Defender permission Defenssive's access lacks
   * (Machine.Read.All, SecurityRecommendation.Read.All, Vulnerability.Read.All or Score.Read.All).
   */
  permission?: string | null
}

export interface FindingsPage {
  workspaceId: string

  /** How current these findings are. Absent from this response until an audit pointed out that
   *  "no open findings match this filter" and "this tenant is clean" were the same sentence. */
  freshness: ScanFreshness
  items: FindingSummary[]

  /** Null when there is no next page. There is no total, deliberately -- see the API. */
  nextCursor: string | null
}

/** The disposition a person put on a finding: what they decided, when, why, and until when. */
export interface FindingDisposition {
  decision: 'suppress' | 'accept_risk' | 'not_applicable' | 'reopen'
  reason: string
  decidedAt: string
  /** Only meaningful for a hiding decision; null means indefinite. On lapse the finding returns on
   *  the next assessment that still sees it — expiry is enforced on re-observation, not autonomously. */
  expiresAt: string | null
  /** Null when the person who decided has since left the workspace. The decision outlives them. */
  decidedBy: string | null
}

/** The working state on a finding. `status` of 'done' is a claim awaiting the next scan, not a resolution. */
export interface FindingWorkflowState {
  status: 'open' | 'in_progress' | 'done'
  /** The owner's email, or null when unassigned. */
  owner: string | null
  /** Indices into `remediation` that have been ticked. */
  completedSteps: number[]
  updatedAt: string
}

/** A page of further reading on a finding's control, from the catalogue. http(s) only, server-checked. */
export interface FindingDocLink {
  title: string
  url: string
}

/**
 * An ACTIVE finding the latest finished assessment (complete or partial) did not re-check: its check
 * waited on a prerequisite (withheld, migration 0152), could not read, or gave no answer the scan
 * recorded. Neither outcome is allowed to resolve a finding, so it stays open with an old last-seen
 * date -- and the page says so rather than letting it pass for one of today's results.
 */
export interface FindingRecheck {
  /** When an assessment last saw this finding (its last-seen). */
  lastCheckedAt: string
  /** When the latest assessment that finished ended. */
  latestAssessmentAt: string
  /** The scan's own reason for this check, or null when it recorded neither of these. */
  reason: 'withheld' | 'could_not_run' | null
  /** For withheld: the finding it waits on (its title, or its key when uncatalogued). */
  waitsOn: { key: string; title: string } | null
}

export interface FindingDetailResponse {
  finding: FindingSummary
  description: string | null
  /** The steps, in order. A jsonb array server-side; it used to arrive as its own JSON text. */
  remediation: string[]
  evidence: { label: string; value: string }[]

  /** Framework references, one per entry (the server splits the catalogue's single string). An older
   *  server sent the whole string as one entry; the page splits again, so both render as chips. */
  references: string[]

  /** The catalogue's further reading, in order. Absent from an older server: treat as none. */
  docLinks?: FindingDocLink[]

  /** Set only when this active finding was not re-checked by the latest finished assessment.
   *  Absent from an older server, and null when it was: no notice either way. */
  recheck?: FindingRecheck | null

  /**
   * Set only on a RESOLVED finding the assessment that closed it closed as not applicable (a
   * federal-only control on a workspace whose organisation profile was not US federal civilian
   * agency, migrations 0175 and 0176, or a control whose check answered not applicable on the
   * tenant's licences), with that assessment's own reason. It was not fixed. Absent from an older
   * server, and null otherwise.
   */
  noLongerApplies?: { reason: string; resolvedAt: string } | null

  /**
   * Headings over groups of the remediation steps (0201), in step order: each starts at `firstStep`, a
   * 0-based index into `remediation`, and runs to the next. Empty for a short checklist; absent from an
   * older server. The steps and their numbering are the same with or without them.
   */
  remediationStages?: { title: string; firstStep: number }[]

  /** The standing decision, or null on a finding nobody has dispositioned. */
  disposition: FindingDisposition | null

  /** The working state, or null on a finding nobody has touched. */
  workflow: FindingWorkflowState | null

  /** The Microsoft admin surface where this is fixed, or null (a DNS or tenant-specific fix). */
  portalUrl: string | null

  /** The CISA SCuBA policy id this finding is raised against (e.g. `MS.AAD.3.2v2`), or null for a
   *  check outside SCuBA. Null means this is an additional check, not a CISA baseline deviation. */
  scubaReference: string | null

  /** Where this finding's evidence originates, ordered (migration 0145) — the EVIDENCE axis, distinct
   *  from the framework (`scubaReference`) and who judged severity. Empty only for an unmapped check;
   *  the detail page shows "Not recorded" then rather than implying an origin. */
  evidenceSources: string[]
}

/** One policy of the CISA SCuBA baseline, and whether this product has a check for it. */
export interface BaselinePolicy {
  /** The published id, e.g. `MS.AAD.3.1v1`. */
  reference: string

  /** CISA's own sentence, transcribed rather than paraphrased. */
  statement: string
  criticality: 'SHALL' | 'SHOULD'

  /**
   * assessed · licence · permission · unavailable · external · not_built (0171).
   *
   * Not a union type, deliberately. TypeScript describes what we expect a response to contain and
   * checks nothing about what arrives, so a status added to the table after this build shipped
   * would be typed out of existence here and dropped from a card whose whole claim is that it
   * shows the entire baseline. The card groups on whatever it is given.
   */
  status: string

  /** Why, when this is not simply assessed. Null only for the ones that are. */
  detail: string | null

  /**
   * Whether a check exists at all. The SERVER's rule, not re-derived here.
   *
   * `licence` counts: those checks run and report not-applicable, with a reason, on a tenant
   * without the licence. Deciding that again on the client would be a second place for the
   * headline number to be computed, and the direction a disagreement would flatter us in is the
   * one this product must never round in.
   */
  hasCheck: boolean
}

/**
 * The version of one SCuBA product's baseline this build measures against.
 *
 * The card names a denominator — "29 of 34" — but 34 is CISA's number and CISA revises it; the
 * policy ids carry v1/v2 suffixes to prove it. This dates the claim. One entry per product, because
 * the baselines are versioned separately and this product transcribed them from different sources.
 */
export interface BaselineVersion {
  /** The SCuBA product code, the id's second segment: `AAD`, `EXO`, … */
  product: string
  name: string
  source: string
  sourceRef: string
  sourceUrl: string

  /** ISO date the baseline was captured, e.g. `2026-09-17`. */
  capturedOn: string

  /** The last release this baseline is ahead of, when it tracks an unreleased branch. Null otherwise. */
  exceedsRelease: string | null

  /** The count the pin vouches for — the denominator this ref was captured with. */
  expectedPolicyCount: number

  /** This product's policy count in the ledger right now, counted server-side. */
  ledgerPolicyCount: number

  /**
   * Whether the pin still matches the ledger. The server's verdict, not re-derived here. False
   * means the ref has moved past the policies it dates, and the card flags the provenance as stale
   * rather than trusting it.
   */
  reconciled: boolean

  /** One provenance sentence, composed on the server so every surface says it the same way. */
  label: string
}

/**
 * What this product looks at, and what it does not.
 *
 * The figure that stands in for the security score this product deliberately does not publish.
 * Note what is absent: no percentage, no grade, no total out of 100. A ratio against a published
 * denominator is checkable against somebody else's document; a score is not.
 */
/** One registered check, as the inventory names it. */
export interface InventoryCheck {
  key: string
  title: string
  domain: string
  scubaReference?: string | null
}

/**
 * The checks THIS deployed build can ATTEMPT, partitioned by their relationship to CISA SCuBA.
 *
 * SUPPORTED, NOT PASSED: every count is of checks the build can attempt — never a coverage score,
 * never a statement that a check ran on this tenant or passed. The three buckets are disjoint and sum
 * to `registeredTotal` (with `uncatalogued`, a defect signal that is empty in a healthy build).
 */
export interface CheckInventory {
  registeredTotal: number
  /** Checks mapped to a policy in the published SCuBA ledger — accounted in the ratio above, count only. */
  scubaInLedger: number
  /** SCuBA-mapped checks whose product is not yet in the published ledger — shown apart, no ratio claimed. */
  scubaMappedOutsideLedger: {
    total: number
    byProduct: Record<string, number>
    checks: InventoryCheck[]
  }
  /** Checks with no SCuBA mapping — additional security checks outside SCuBA, grouped by domain. */
  additional: {
    total: number
    byDomain: Record<string, number>
    checks: InventoryCheck[]
  }
  /**
   * Checks mapped to a SCuBA policy the ledger does not contain although it DOES cover that product —
   * a mapping the ledger cannot back. A defect, surfaced (empty when healthy) so it is never quietly
   * shown as "product not yet in the ledger".
   */
  mappedToUnknownPolicy: InventoryCheck[]
  /** Registered checks with no catalogue row — a data defect, surfaced not hidden (empty when healthy). */
  uncatalogued: string[]
}

export interface Coverage {
  /** Every policy in the baseline, whether or not we assess it. */
  total: number

  /** How many have a check. NOT "assessed" — see `hasCheck`. */
  withCheck: number
  byStatus: Record<string, number>
  policies: BaselinePolicy[]

  /**
   * The baseline version(s) the ledger is measured against, one per product. May be empty from an
   * older server; the card renders nothing rather than inventing a version when it is.
   */
  baseline: BaselineVersion[]

  /**
   * The inventory of checks this build can attempt, partitioned by SCuBA relationship. Optional so an
   * older server that does not send it renders no block rather than an empty one.
   */
  checkInventory?: CheckInventory
}

export async function getOverview(): Promise<ApiResult<Overview>> {
  return request<Overview>('/api/overview')
}

/** Whether a stamped/sealed version still matches the current one. */
export type VersionDrift = 'unknown' | 'current' | 'changed'

/** One SCuBA product's baseline as it was pinned at scan time. */
export interface ReportBaselineVersion {
  product: string
  baselineName: string
  source: string
  sourceRef: string
  sourceUrl: string
  capturedOn: string
  exceedsRelease: string | null
  pinnedPolicyCount: number
  reconciledAtStart: boolean
  drift: VersionDrift
}

/**
 * A cheap, UNAUTHENTICATED existence hint for the latest sealed report (plaintext columns, no decrypt).
 * It lets the page offer the download and show an as-recorded date; it deliberately carries no integrity
 * reference — the record is verified (fingerprint + GCM) only on download, where a failed one is refused.
 */
export interface SealedReportSummary {
  available: true
  scanStatus: string
  sealedAt: string
  findingCount: number
  capped: boolean
  catalogDrift: VersionDrift
}

/**
 * The versions the latest scan ran under, with live drift, and whether a tamper-evident sealed copy
 * exists. Lets the report state which rules it reflects and disclose if they have since moved.
 */
export interface ReportVersion {
  /** Which workspace answered. Compared against the session, never sent by the client. */
  workspaceId: string
  /** False when the latest scan predates versioning, or there is no scan — an honest "not recorded". */
  recorded: boolean
  catalogFingerprint: string | null
  catalogFingerprintShort: string | null
  catalogReconciled: boolean
  scannerRulesetVersion: string | null
  changedDuringScan: boolean
  catalogDrift: VersionDrift
  baselines: ReportBaselineVersion[]
  /** Null when no completed or partial scan has sealed a report yet. */
  sealed: SealedReportSummary | null
}

export async function getReportVersion(): Promise<ApiResult<ReportVersion>> {
  return request<ReportVersion>('/api/reports/version')
}

/**
 * One action on the report's executive page, in the order the Recommendations page gives them:
 * its "Start here" list first (Critical or High, Low effort), then severity and effort.
 */
export interface ExecutiveAction {
  id: string
  title: string
  area: string
  severity: string
  effort: string
  workstream: string | null
  /** The finding's own distinguishing line, as lists show it. Null for most. */
  summary: string | null
  /** Critical or High, and Low effort: the Recommendations page's "Start here" rule. */
  startHere: boolean
  /**
   * One sentence quoted from the catalogue's "What this means": the first, or the second when the
   * first only restates the title (the catalogue says what is wrong, then why it matters). Null when
   * the catalogue has no text: the title then stands alone. Never written for the report.
   */
  meaningLine: string | null
  /**
   * The finding's working-state owner. `assigned` with a null `email` is an owner this workspace
   * can no longer see (they have left it) -- assigned, never "Unassigned". There is no target date:
   * findings do not have one.
   */
  owner: { assigned: boolean; email: string | null; activeMember: boolean }
}

/** A finished (complete or partial) assessment, as the change section names it. */
export interface FinishedAssessment {
  scanId: string
  status: string
  completedAt: string
  areasTotal: number
  areasCompleted: number
  /**
   * False when it was not allowed to resolve anything: no check in it read its evidence in full, it
   * was stopped part-way, or its access changed while it ran (the server does not say which). It
   * then resolved nothing. True does not mean it resolved something.
   */
  mayResolve: boolean
}

/** One finding named in the change section: titles and bands only. */
export interface ChangedFinding {
  id: string
  title: string
  area: string
  severity: string
  /** How a reopened finding came back: 'reappeared' or 'suppression_expired'. Null otherwise. */
  reopened: string | null
  /**
   * For a finding that reappeared, how it had been closed before: 'resolved', or 'not_applicable'
   * (closed as not applicable, under the organisation profile or because of the tenant's licences,
   * migration 0176). Null for anything
   * that did not reappear (a suppression that lapsed included). Absent from an older server, or null
   * when no closing row precedes the reappearance; in both of those cases the earlier closure was a
   * resolution.
   */
  closedAs?: 'resolved' | 'not_applicable' | null
}

/** A change list: the exact count, and the findings it names (the server stops naming at 50). */
export interface ChangeList {
  count: number
  items: ChangedFinding[]
}

/** What changed between the latest finished assessment and the one before it. */
export interface AssessmentChanges {
  assessment: FinishedAssessment
  /** Null when this is the first assessment that finished: nothing to compare with. */
  previous: FinishedAssessment | null
  /** First seen since the previous assessment finished, and still open. */
  new: ChangeList
  /** Resolved by this assessment, and not seen since. */
  resolved: ChangeList
  /**
   * Seen again since the previous assessment (after being resolved or closed as not applicable, or
   * after a set-aside lapsed), and still open.
   */
  reopened: ChangeList
  /**
   * Closed by this assessment as not applicable (migration 0176), under the organisation profile or
   * because of the tenant's licences, and still closed. Never part of `resolved`: nothing was seen to
   * be fixed. Absent from an older server.
   */
  noLongerApplies?: ChangeList
}

/** The report's executive first page. Coverage comes from the overview, which the page already reads. */
export interface ReportSummary {
  /** Which workspace answered. Compared against the session, never sent by the client. */
  workspaceId: string
  actions: ExecutiveAction[]
  /** Null when no assessment has finished yet. */
  changes: AssessmentChanges | null
}

export async function getReportSummary(): Promise<ApiResult<ReportSummary>> {
  return request<ReportSummary>('/api/reports/summary')
}

/**
 * The coverage ledger.
 *
 * No workspace in the response, because there is nothing tenant-shaped in it: these are CISA's
 * published sentences and they are the same for every customer. The read still requires a
 * session, like every other route.
 */
export async function getCoverage(): Promise<ApiResult<Coverage>> {
  return request<Coverage>('/api/coverage')
}

export async function getFindings(options: {
  status?: string
  severity?: string
  area?: string
  /** Narrow to one effort band. The server accepts Low / Medium / High. */
  effort?: string
  sort?: 'lastSeen' | 'severity' | 'impact'
  cursor?: string

  /**
   * How many rows to ask for. The server clamps this to 100 and defaults it to 25.
   *
   * Only the dashboard passes it. It needs one worst-first read to fill four severity cards and
   * an ordered "fix these first" list, and twenty-five rows of Critical and High leave the lower
   * levels with no titles at all — which would render a card reading "11" above nothing, in the
   * place the design puts the three titles that answer "does this matter".
   */
  pageSize?: number

  /** A finding reference, whole or the eight characters the product shows. */
  reference?: string
} = {}): Promise<ApiResult<FindingsPage>> {
  const query = new URLSearchParams()

  // Only the keys the server whitelists. Sending anything else would be dropped there anyway;
  // not sending it keeps the two ends honest about what the contract actually is.
  if (options.status) query.set('status', options.status)
  if (options.severity) query.set('severity', options.severity)
  if (options.area) query.set('area', options.area)
  if (options.effort) query.set('effort', options.effort)
  if (options.sort === 'severity' || options.sort === 'impact') query.set('sort', options.sort)
  if (options.cursor) query.set('cursor', options.cursor)
  if (options.pageSize) query.set('pageSize', String(options.pageSize))
  if (options.reference) query.set('reference', options.reference)

  const suffix = query.toString()
  return request<FindingsPage>(`/api/findings${suffix ? `?${suffix}` : ''}`)
}

/**
 * How many characters of a finding's id the product shows and asks people to quote.
 *
 * ONE DEFINITION. This was written out as `id.slice(0, 8)` wherever a reference appeared, and a
 * reference is a promise: the eight characters printed on a list, shown on a detail page, typed
 * into the search box and pasted into a url all have to be the same eight, or a customer quotes
 * something the product cannot find. The server accepts 4 to 31 characters, so this can be
 * lengthened here alone if collisions ever stop being rare.
 */
export const REFERENCE_LENGTH = 8

/** The reference for a finding id — what a customer sees and quotes back. */
export function referenceOf(id: string): string {
  return id.slice(0, REFERENCE_LENGTH)
}

/**
 * Text shaped like a finding reference (the hex characters on a finding's page) or a whole finding
 * id: the two forms GET /api/findings/{idOrReference} takes, and what the searches treat as one.
 */
export const FINDING_REFERENCE =
  /^(?:[0-9a-f]{4,31}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

/**
 * One finding, by its whole id or by the reference the customer was shown.
 *
 * The server takes both: a full uuid hits the guid route, and a shorter hex string hits the
 * reference route, which resolves it and refuses rather than guesses when two findings share a
 * prefix. That is what lets a finding have a url worth sending to somebody.
 */
export async function getFinding(
  idOrReference: string,
): Promise<ApiResult<FindingDetailResponse>> {
  return request<FindingDetailResponse>(`/api/findings/${encodeURIComponent(idOrReference)}`)
}

/**
 * Record a disposition on a finding.
 *
 * A decision suppresses; only a scan resolves — so this takes a finding off the active list (or,
 * on reopen, brings it back), and never marks it resolved. `reason` is required for everything but
 * reopen; `expiresAt` (ISO) is honoured only for a hiding decision and only in the future. The id
 * must be a whole guid, not the short reference.
 */
export async function decideFinding(
  id: string,
  body: {
    decision: 'suppress' | 'accept_risk' | 'not_applicable' | 'reopen'
    reason?: string
    expiresAt?: string | null
  },
): Promise<ApiResult<{ recorded: true; decision: string; status: string }>> {
  return request(`/api/findings/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Replace the working state on a finding — owner, status, ticked steps. A full replace: send the
 * whole intended state. `ownerEmail` empty or omitted unassigns; it must be an active member.
 */
export async function setFindingWorkflow(
  id: string,
  body: {
    workStatus: 'open' | 'in_progress' | 'done'
    ownerEmail?: string | null
    completedSteps?: number[]
  },
): Promise<ApiResult<{ saved: true }>> {
  return request(`/api/findings/${encodeURIComponent(id)}/workflow`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/** One person who can reach this workspace. */
/** A workspace role. 'owner' is never invitable and never a valid target of a role change. */
export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'read_only'

/** A membership's state: an active member, a pending invitation, or a removed (suspended) one. */
export type MemberStatus = 'active' | 'pending' | 'suspended'

export interface WorkspaceMember {
  email: string
  role: WorkspaceRole
  status: MemberStatus
}

/** The Microsoft tenant this workspace assesses. */
export interface WorkspaceConnection {
  /**
   * The directory id, shown on purpose. It is not a secret -- it is in every consent URL the
   * customer has clicked -- and it is the one value that lets an administrator confirm, without
   * trusting us, that the tenant being read is the tenant they meant.
   */
  tenantId: string
  status: string
  verifiedAt: string | null
}

/** A workspace's own settings. Everything read, nothing derived. */
export interface WorkspaceSettings {
  workspaceId: string
  /** Server-scoped pilot wording hint, not request authority. Absent/false hides Review copy. */
  recordedEvidenceReviewAvailable?: boolean
  name: string
  plan: Plan
  planStatus: PlanStatus
  dataRegion: string
  verificationState: string
  verifiedAt: string | null

  /** Null when no tenant has been connected — render that, not an empty row. */
  connection: WorkspaceConnection | null
  members: WorkspaceMember[]

  /**
   * What kind of organisation this is (migration 0175), as stored. Every workspace starts as
   * 'commercial'. Absent from an older server: the page says it could not be read rather than
   * guessing, because the profile decides whether a control is assessed at all.
   */
  organisationProfile?: string

  /** The controls the profile decides, from the checks' own declaration. Absent from an older server. */
  organisationProfileControls?: ProfileControl[]
}

/** The organisation profiles, in the order the settings page offers them (migration 0175). */
export const ORGANISATION_PROFILES = [
  {
    value: 'commercial',
    label: 'Commercial organisation',
    what: 'A business, charity or other private organisation.',
  },
  {
    value: 'us_federal_civilian',
    label: 'US federal civilian agency',
    what: 'A department or agency of the US federal civilian executive branch, which CISA’s directives apply to.',
  },
  {
    value: 'other_public_sector',
    label: 'Other public sector',
    what: 'Any other government body, such as a state, local or tribal government, or a government outside the US.',
  },
] as const

export type OrganisationProfile = (typeof ORGANISATION_PROFILES)[number]['value']

/** Whether a stored word is one of the three profiles. Anything else is unknown, never a guess. */
export function isOrganisationProfile(value: unknown): value is OrganisationProfile {
  return ORGANISATION_PROFILES.some((p) => p.value === value)
}

/**
 * A control the organisation profile decides: assessed only for a US federal civilian agency,
 * reported not applicable for any other profile.
 */
export interface ProfileControl {
  key: string
  title: string
  scubaReference: string | null
}

/** What an invitation may grant. Never 'owner' — the database refuses that by name. */
export const INVITABLE_ROLES = [
  {
    value: 'admin',
    label: 'Admin',
    what: 'Reads everything, and can run assessments and change the Microsoft tenant connection.',
  },
  {
    value: 'member',
    label: 'Member',
    what: 'Reads everything. Cannot run assessments or change the tenant connection.',
  },
  {
    value: 'read_only',
    label: 'Read-only auditor',
    // The SAME access as Member today (the server's only line is owner/admin versus everyone else),
    // and it says so. Two identical descriptions made the choice look meaningful when it is a label;
    // claiming a restriction that does not exist would be worse.
    what:
      'Same access as Member today: reads everything, cannot run assessments or change the ' +
      "tenant connection. Use it to mark an external auditor's seat.",
  },
] as const

/** The roles a member can be invited to or changed to — everything except 'owner', derived from the
 *  one list so a new role cannot appear in a request or a picker without being added here. */
export type InvitableRole = (typeof INVITABLE_ROLES)[number]['value']

export interface InviteOutcome {
  invited: boolean
  email: string
  role: string
}

/**
 * A member-management mutation (remove / withdraw / role change).
 *
 * Mirrors inviteMember rather than the generic request(): every refusal is a specific sentence the
 * database wrote (you are not an owner, that person is not an active member, an owner cannot be
 * removed), and reducing it to a status would make the page guess which. `error` is kept alongside
 * `message` because several distinct conditions share HTTP 409 and the caller uses it to recognise a
 * STALE row (a 404/409 means the row it acted on is out of date and the list should be refreshed).
 */
export type SettingsMutation =
  | { ok: true }
  | { ok: false; status: number; error?: string; message: string }

async function memberAction(
  path: string,
  body: Record<string, unknown>,
  fallback: string,
): Promise<SettingsMutation> {
  try {
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (response.ok) return { ok: true }

    // Not request(): the refusal body is the point. Early 401/cross-origin refusals can arrive
    // without a message, so a specific fallback stands in.
    const parsed = (await response.json().catch(() => null)) as
      | { error?: unknown; message?: unknown }
      | null
    return {
      ok: false,
      status: response.status,
      error: typeof parsed?.error === 'string' ? parsed.error : undefined,
      message:
        typeof parsed?.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : fallback,
    }
  } catch {
    return { ok: false, status: 0, message: 'Could not reach the server.' }
  }
}

/** What Settings shows about Azure (Azure CSPM, slice 4). */
export interface AzureSettings {
  workspaceId: string
  /** Azure is assessed on the Monitor plan only; off it, Azure is not contacted. */
  onMonitor: boolean
  /** The subscriptions the Reader role exposes. Null when not read (off Monitor, or Azure refused). */
  subscriptions: { id: string; name: string; state: string }[] | null
  /** One short word when the subscriptions could not be read (no_token, http_403, ...). */
  readFailure: string | null
  /** The latest Azure assessment, of any outcome. */
  lastRun: { ranAt: string; outcome: string; subscriptionCount: number } | null
}

/** 404 while Azure assessment is switched off: Settings then shows no Azure tab. */
export async function getAzureSettings(): Promise<ApiResult<AzureSettings>> {
  return request('/api/workspace/azure')
}

/** Bring-your-own AI: the workspace's own Azure OpenAI (migration 0195). There is no key. */
export interface AiSettings {
  workspaceId: string
  /** Server-scoped pilot wording hint, not request authority. Absent/false hides Review copy. */
  recordedEvidenceReviewAvailable?: boolean
  /** AI is part of the Monitor plan; off it nothing is saved or tested. */
  onMonitor: boolean
  limits: { minCap: number; maxCap: number; defaultCap: number }
  settings: {
    azureResource: string
    azureDeployment: string
    dailyCap: number
    evidenceLevel: 'redacted' | 'none' | string
    acknowledgedBy: string | null
    acknowledgedAt: string
    configuredAt: string
    lastTestedAt: string | null
    /** ok, no_token, forbidden, not_found, throttled, unreachable, unexpected. */
    lastTestOutcome: string | null
    lastTestModel: string | null
  } | null
}

/** 404 while bring-your-own AI is switched off: Settings then shows no AI tab. */
export async function getAiSettings(): Promise<ApiResult<AiSettings>> {
  return request('/api/workspace/ai')
}

/** Owner only, Monitor only; the server and the database both decide. */
export async function saveAiSettings(body: {
  azureResource: string
  azureDeployment: string
  dailyCap: number
  evidenceLevel: string
  acknowledged: boolean
}): Promise<SettingsMutation> {
  return memberAction('/api/workspace/ai', body, 'The AI settings could not be saved.')
}

/** One fixed request to the saved deployment, carrying no customer data. */
export async function testAi(): Promise<SettingsMutation> {
  return memberAction('/api/workspace/ai/test', {}, 'The test could not be run.')
}

/** Turns AI off for the workspace. Works on any plan. */
export async function removeAiSettings(): Promise<SettingsMutation> {
  return memberAction('/api/workspace/ai/remove', {}, 'The AI settings could not be removed.')
}

/** One AI answer on a finding: text with placeholders, and the real value of each, for the browser to put back. */
export interface FindingAiAnswer {
  kind: 'explain' | 'fix_plan' | string
  text: string
  placeholders: Record<string, string>
  model: string | null
  createdAt: string
}

export interface FindingAi {
  verificationEnabled?: boolean
  canReview?: boolean
  verificationAcknowledgementVersion?: string | null
  verificationAcknowledgementText?: string | null
  workspaceId: string
  onMonitor: boolean
  configured: boolean
  usedToday: number | null
  dailyCap: number | null
  answers: FindingAiAnswer[]
}

/** 404 while bring-your-own AI is switched off: the finding page then shows nothing about AI. */
export async function getFindingAi(findingId: string): Promise<ApiResult<FindingAi>> {
  return request(`/api/findings/${encodeURIComponent(findingId)}/ai`)
}

/** Explain or Fix plan. Not request(): a refusal carries its outcome word, which the page words. */
export async function askFindingAi(
  findingId: string,
  kind: 'explain' | 'fix_plan' | 'verify_evidence',
  verificationAcknowledged = false,
  verificationAcknowledgementVersion?: string,
): Promise<{ ok: true; answer: FindingAiAnswer } | { ok: false; status: number; outcome?: string }> {
  try {
    const response = await fetch(`/api/findings/${encodeURIComponent(findingId)}/ai`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, ...(kind === 'verify_evidence'
        ? { verificationAcknowledged, verificationAcknowledgementVersion } : {}) }),
    })
    const parsed = (await response.json().catch(() => null)) as { outcome?: unknown; answer?: FindingAiAnswer } | null
    if (response.ok && parsed?.answer) return { ok: true, answer: parsed.answer }
    return { ok: false, status: response.status, outcome: typeof parsed?.outcome === 'string' ? parsed.outcome : undefined }
  } catch {
    return { ok: false, status: 0 }
  }
}

/** An Identity finding an AI inventory entry points to, rather than repeating it. */
export interface AiInventoryEntryFinding {
  id: string
  catalogKey: string
  title: string
  severity: string
  /** What within the asset it is about, when narrower: a model deployment's name. Absent from an older server. */
  subject?: string | null
}

/** The AI page's inventory: AI applications from other publishers, and agents. Off Monitor: onMonitor false, nothing else. */
export interface AiInventory {
  workspaceId: string
  onMonitor: boolean
  complete: boolean
  /** More applications or agents were found than are listed. Absent from an older server. */
  truncated?: boolean
  apps: {
    id: string
    appId: string
    name: string
    publisherTenant: string | null
    organisationWideScopes: string[]
    personalScopes: string[]
    applicationPermissions: string[]
    /** False when its consents or permissions could not be read. Absent from an older server. */
    permissionsRead?: boolean
    findings: AiInventoryEntryFinding[]
  }[]
  agents: {
    id: string
    /** Its application id, for the link to its Microsoft Entra page. Absent from an older server. */
    appId?: string | null
    name: string
    kind: string
    applicationPermissions: string[]
    directoryRoles: string[]
    /** False when its permissions or roles could not be read. Absent from an older server. */
    permissionsRead?: boolean
    findings: AiInventoryEntryFinding[]
  }[]

  /**
   * The Azure OpenAI and AI Foundry resources (the AI assets page, 2026-10-12), read live with Azure Reader. Null where
   * this build reads none; absent from an older server. outcome: completed, no_subscriptions, no_token or unreadable.
   */
  azure?: {
    outcome: string
    partial: boolean
    accounts: {
      id: string
      name: string
      kind: string
      location: string | null
      resourceGroup: string | null
      keySignIn: boolean
      publicNetwork: 'open' | 'selected_networks' | 'disabled'
      privateEndpoints: number
      /** Diagnostic settings that send logs; null when they could not be read. */
      loggingSettings: number | null
      deployments: { name: string; model: string | null; modelVersion: string | null; contentFilter: string | null }[] | null
      findings: AiInventoryEntryFinding[]
    }[]
    hubs: {
      id: string
      name: string
      kind: string
      location: string | null
      resourceGroup: string | null
      publicNetwork: 'open' | 'selected_networks' | 'disabled'
      findings: AiInventoryEntryFinding[]
    }[]
  } | null
}

/** 404 while the AI area is switched off. */
export async function getAiInventory(): Promise<ApiResult<AiInventory>> {
  return request('/api/ai/inventory')
}

/** One emergency (break-glass) or service account the owner has named (migration 0154). */
export interface ExemptAccount {
  id: string
  objectId: string
  userPrincipalName: string
  displayName: string | null
  kind: 'emergency' | 'service' | string
  reason: string
  addedBy: string | null
  addedAt: string
}

/** The workspace's emergency and service accounts. Any member may read them. */
export async function getExemptAccounts(): Promise<ApiResult<{ workspaceId: string; accounts: ExemptAccount[] }>> {
  return request('/api/workspace/exempt-accounts')
}

/**
 * List an emergency or service account, by the address it signs in with. Owner-only; the server
 * resolves the address in the workspace's own Microsoft tenant.
 */
export function addExemptAccount(body: {
  userPrincipalName: string
  kind: 'emergency' | 'service'
  reason: string
}): Promise<SettingsMutation> {
  return memberAction('/api/workspace/exempt-accounts', body, 'That account could not be added.')
}

/** Remove a listed account. Owner-only. */
export function removeExemptAccount(id: string): Promise<SettingsMutation> {
  return memberAction('/api/workspace/exempt-accounts/remove', { id }, 'That account could not be removed.')
}

/**
 * Change the workspace's organisation profile. Owner or admin; the server decides, audits it, and it
 * takes effect on the next assessment.
 */
export function setOrganisationProfile(profile: OrganisationProfile): Promise<SettingsMutation> {
  return memberAction(
    '/api/workspace/organisation-profile', { profile }, 'The organisation profile could not be changed.')
}

/** Remove (suspend) an ACTIVE member — ends their sessions server-side. Owner-only; owners refused. */
export function removeMember(email: string): Promise<SettingsMutation> {
  return memberAction('/api/workspace/members/remove', { email }, 'That member could not be removed.')
}

/** Withdraw a PENDING invitation. Owner-only. */
export function withdrawInvitation(email: string): Promise<SettingsMutation> {
  return memberAction(
    '/api/workspace/members/withdraw', { email }, 'That invitation could not be withdrawn.')
}

/** Change an ACTIVE member's role (never to or from owner). Owner-only. */
export function changeMemberRole(email: string, role: InvitableRole): Promise<SettingsMutation> {
  return memberAction(
    '/api/workspace/members/role', { email, role }, "That person's role could not be changed.")
}

/**
 * Step 1 of closing this workspace: the server records the intent and returns a single-use confirmation
 * token (valid for 5 minutes). Owner-only; refusals carry the server's sentence. Nothing is closed yet.
 */
export async function beginWorkspaceClosure(): Promise<
  { ok: true; token: string } | { ok: false; status: number; message: string }
> {
  try {
    const response = await fetch('/api/workspace/closure/begin', {
      method: 'POST',
      credentials: 'same-origin',
    })
    const parsed = (await response.json().catch(() => null)) as
      | { confirmationToken?: unknown; message?: unknown }
      | null
    if (response.ok && typeof parsed?.confirmationToken === 'string') {
      return { ok: true, token: parsed.confirmationToken }
    }
    return {
      ok: false,
      status: response.ok ? 500 : response.status,
      message:
        typeof parsed?.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : 'Closing this workspace could not be started.',
    }
  } catch {
    return { ok: false, status: 0, message: 'Could not reach the server.' }
  }
}

/**
 * Step 2: redeem the confirmation token and close the workspace — permanently, for everyone. On success
 * every session (including this one) has been revoked, so the caller must navigate away.
 */
export async function closeWorkspace(confirmationToken: string): Promise<SettingsMutation> {
  try {
    const response = await fetch('/api/workspace/closure', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmationToken }),
    })
    if (response.ok) return { ok: true }
    const parsed = (await response.json().catch(() => null)) as
      | { error?: unknown; message?: unknown }
      | null
    return {
      ok: false,
      status: response.status,
      error: typeof parsed?.error === 'string' ? parsed.error : undefined,
      message:
        typeof parsed?.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : 'The workspace could not be closed.',
    }
  } catch {
    return { ok: false, status: 0, message: 'Could not reach the server.' }
  }
}

export type NotificationChannel = 'digest' | 'alerts'
export type NotificationPreference = 'default' | 'enabled' | 'disabled'

/**
 * A member's OWN notification preferences for this workspace.
 *
 * `eligible` is the ROLE/PREFERENCE decision about being in the audience (owners/admins are in by
 * default; others must opt in; anyone 'disabled' is out). It is NOT a promise that an email is being
 * sent — that also needs Monitor to be active, a report-worthy period or event, the address not
 * suppressed, and a successful send. The UI keeps those apart.
 */
export interface NotificationPreferences {
  /** The workspace these belong to — compared against the session before any control is rendered. */
  workspaceId: string
  role: string
  /** Whether Monitor (the plan these emails are part of) is active on this workspace right now. */
  monitoringActive: boolean
  digest: { preference: NotificationPreference; eligible: boolean }
  alerts: { preference: NotificationPreference; eligible: boolean }
  /** Whether notification mail to MY address is paused by provider feedback (a hard bounce, a spam
   *  complaint, a manual suppression). Independent of the preference. Never pauses sign-in mail. Optional:
   *  an older API omits it, and then the card makes no claim either way. */
  delivery?: {
    paused: boolean
    reason: 'hard_bounce' | 'spam_complaint' | 'manual_suppression' | string | null
    since: string | null
  }
}

/** My notification preferences. Scope is the session — no workspace/user id is sent. */
export function getNotificationPreferences(): Promise<ApiResult<NotificationPreferences>> {
  return request<NotificationPreferences>('/api/notifications')
}

/**
 * Set one of MY notification preferences. Returns the server's refusal MESSAGE on failure, like the
 * member writes, so the shared settings controller can key its refresh on 401/403/404/409 and a card
 * can surface the sentence verbatim. No workspace/user id is sent — the request capability is the actor.
 */
export async function setNotificationPreference(
  channel: NotificationChannel,
  preference: NotificationPreference,
  expectedWorkspaceId: string,
): Promise<SettingsMutation> {
  try {
    const response = await fetch('/api/notifications', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      // expectedWorkspaceId is a non-authoritative precondition: the server compares it to the scope it
      // mints from the cookie and refuses (409) if a workspace switch in another tab moved it.
      body: JSON.stringify({ channel, preference, expectedWorkspaceId }),
    })

    if (response.ok) return { ok: true }

    const parsed = (await response.json().catch(() => null)) as
      | { error?: unknown; message?: unknown }
      | null
    return {
      ok: false,
      status: response.status,
      error: typeof parsed?.error === 'string' ? parsed.error : undefined,
      message:
        typeof parsed?.message === 'string' && parsed.message.length > 0
          ? parsed.message
          : 'That notification preference could not be saved.',
    }
  } catch {
    return { ok: false, status: 0, message: 'Could not reach the server.' }
  }
}

/**
 * Invites somebody into this workspace.
 *
 * Every rule about who may do this lives in the database. This sends what was typed and reports
 * what came back — the refusals arrive as messages written server-side, and paraphrasing them
 * here is how the two drift apart.
 */
export async function inviteMember(
  email: string,
  role: string,
): Promise<{ ok: true } | { ok: false; status: number; message: string }> {
  try {
    const response = await fetch('/api/workspace/members', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    })

    if (response.ok) return { ok: true }

    /*
     * NOT request(), which discards the body on a failure and returns only a status.
     *
     * That is right for reads -- a findings list that 500s has nothing to say -- and wrong here.
     * Every refusal from this endpoint is a specific sentence the database wrote: you are not an
     * owner, that person is already a member, this workspace has no verified tenant. Reduced to
     * "403" the page would have to guess which, and the guess is the policy written a second
     * time in the one place that cannot see it.
     */
    const body = await response.json().catch(() => null)

    return {
      ok: false,
      status: response.status,
      message:
        typeof body?.message === 'string' && body.message.length > 0
          ? body.message
          : 'That invitation could not be sent.',
    }
  } catch {
    return { ok: false, status: 0, message: 'Could not reach the server.' }
  }
}

export async function getWorkspace(): Promise<ApiResult<WorkspaceSettings>> {
  return request<WorkspaceSettings>('/api/workspace')
}

/**
 * A finding a completed assessment stopped seeing -- or, with `closedAs` 'not_applicable', one it
 * closed as not applicable (migration 0176), under the organisation profile or because of the tenant's
 * licences, which is never shown as fixed.
 *
 * `resolvedAt` is the field that makes this readable: FindingSummary carries first-seen and
 * last-seen, and neither of them is when the finding went away — both describe when we could
 * still see it. `finding.resolved_at` has existed in the schema since migration 0013 and was
 * returned by nothing.
 */
export interface ResolvedFinding extends FindingSummary {
  /**
   * When reconciliation marked it resolved. ISO 8601, like every other instant here.
   *
   * OPTIONAL ON PURPOSE. It is null on every open finding, and absent altogether from a server
   * built before it was added — so the panel renders the finding either way and simply omits the
   * date it does not have, rather than printing "Invalid Date" over a real resolved finding.
   */
  resolvedAt?: string | null
}

export interface ResolvedFindings {
  /** Which workspace answered. Compared against the session, never sent by the client. */
  workspaceId: string

  /** Most recently resolved first. The panel shows the first few and does not page. */
  items: ResolvedFinding[]
}

/** How many the dashboard's panel asks for. It lists a few; it is not a history page. */
const RESOLVED_PANEL_SIZE = 5

/**
 * What has been resolved lately.
 *
 * A filter on the findings list rather than a route of its own, because that is where the
 * capability, the workspace check and the closed set of statuses already live — `status=resolved`
 * has been accepted by the server since findings were first listable, and the field this needed
 * (`resolvedAt`) is being added to the summary every list item already carries.
 *
 * Sorted by last-seen rather than by resolution, because last-seen is the sort the server offers
 * and for a RESOLVED finding the two agree: nothing bumps its last_seen_at again, so the most
 * recently seen of them is the most recently resolved. That equivalence is why this is not
 * approximate, and it stops holding the moment a resolved finding can be re-observed without
 * being reopened — if that ever becomes possible, this needs a sort of its own.
 *
 * Wrapped in a function of its own rather than called inline so that if the endpoint does move to
 * a route of its own, this is the only thing that changes.
 */
export async function getResolvedFindings(): Promise<ApiResult<ResolvedFindings>> {
  const result = await getFindings({
    status: 'resolved',
    sort: 'lastSeen',
    pageSize: RESOLVED_PANEL_SIZE,
  })

  if (!result.ok) return result

  // Defensive about the field being mid-flight. A response missing `items` would otherwise throw
  // inside a .map() at render, taking the whole dashboard down over a panel that is meant to
  // degrade quietly.
  return {
    ok: true,
    data: {
      workspaceId: result.data.workspaceId,
      items: Array.isArray(result.data.items) ? (result.data.items as ResolvedFinding[]) : [],
    },
  }
}
